// Pinia store for open overlays (modals and flyouts).
import '@types'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import zIndexes from '@/assets/styles/zIndexes.module.scss'
import useAnalytics from '@shared/composables/analytics.js'

/**
 * @typedef {{
 *   name: string,
 *   context: Record<string, unknown>,
 *   zIndex: number
 * }} Overlay
*/

/** @typedef {Ref<{ [name: string]: Overlay }>} OverlaySet */

export const useOverlaysStore = defineStore('overlays', () => {
  /** @type {OverlaySet} */
  const modals = ref({})
  /** @type {OverlaySet} */
  const flyouts = ref({})
  /** @type {OverlaySet} */
  /**
 * @typedef {Overlay & {
 *   minimized: boolean,
  * }} Drawer
  */
  /** @typedef {Ref<{ [name: string]: Drawer }>} DrawerSet */
  const drawers = ref({})
  const drawerIsDragging = ref(false)
  // Scroll positions saved when navigating while an overlay is open.
  const savedScrollPositions = ref([])
  const { trackOverlayInteraction } = useAnalytics()

  const modalName = computed(() => latestModal.value?.name ?? null)
  const modalContext = computed(() => latestModal.value?.context ?? null)
  const modalZIndex = computed(() => latestModal.value?.zIndex ?? null)

  const flyoutName = computed(() => latestFlyout.value?.name ?? null)
  const flyoutContext = computed(() => latestFlyout.value?.context ?? null)
  const flyoutZIndex = computed(() => latestFlyout.value?.zIndex ?? null)

  const drawerName = computed(() => latestDrawer.value?.name ?? null)
  const drawerContext = computed(() => latestDrawer.value?.context ?? null)
  const drawerZIndex = computed(() => latestDrawer.value?.zIndex ?? null)
  const drawerMinimized = computed(() => latestDrawer.value?.minimized ?? false)
  const isAnyModalOpen = computed(() => Object.keys(modals.value).length > 0)
  const isAnyFlyoutOpen = computed(() => Object.keys(flyouts.value).length > 0)
  const isAnyDrawerOpen = computed(() => {
    const openDrawers = Object.values(drawers.value).filter(drawer => !drawer.minimized)
    return openDrawers.length > 0
  })
  const isAnyDrawerPresent = computed(() => {
    return Object.keys(drawers.value).length > 0
  })
  const isAnyOverlayOpen = computed(() => isAnyModalOpen.value || isAnyFlyoutOpen.value || isAnyDrawerOpen.value)

  const latestModal = computed(() => {
    // Return the modal with the highest z-index
    return Object.values(modals.value).reduce((acc, modal) => {
      return acc && acc.zIndex > modal.zIndex ? acc : modal
    }, null)
  })
  const latestFlyout = computed(() => {
    // Return the flyout with the highest z-index
    return Object.values(flyouts.value).reduce((acc, flyout) => {
      return acc && acc.zIndex > flyout.zIndex ? acc : flyout
    }, null)
  })
  const latestDrawer = computed(() => {
    // Return the drawer with the highest z-index
    return Object.values(drawers.value).reduce((acc, drawer) => {
      return acc && acc.zIndex > drawer.zIndex ? acc : drawer
    }, null)
  })
  const latest = computed(() => {
    const all = [latestModal.value, latestFlyout.value, latestDrawer.value]
      .filter(Boolean)
      .sort((a, b) => b.zIndex - a.zIndex)
  
    if (!all.length) return null
  
    const top = all[0]
    if (top === latestModal.value) return { type: 'modal', ...top }
    if (top === latestFlyout.value) return { type: 'flyout', ...top }
    if (top === latestDrawer.value) return { type: 'drawer', ...top }
    return null
  })
  
  const zIndexAboveIntercom = parseInt(zIndexes['above-intercom'])
  const baseModalZIndex = parseInt(zIndexes.modal)
  const baseFlyoutZIndex = zIndexAboveIntercom
  const baseDrawerZIndex = parseInt(zIndexes.drawer)
  const latestZIndex = computed(() => latest.value?.zIndex ?? null)
  const nextModalZIndex = computed(() => latestZIndex.value >= baseModalZIndex ? latestZIndex.value + 1 : baseModalZIndex)
  const nextFlyoutZIndex = computed(() => latestZIndex.value >= baseFlyoutZIndex ? latestZIndex.value + 1 : baseFlyoutZIndex)
  const nextDrawerZIndex = computed(() =>
    latestZIndex.value >= baseDrawerZIndex ? latestZIndex.value + 1 : baseDrawerZIndex
  )
  

  
  function isModalOpen (name) {
    return !!modals.value[name]
  }
  function isFlyoutOpen (name) {
    return !!flyouts.value[name]
  }
  function openModal ({ name, context, options = {} }) {
    // Currently, we only track one open modal at a time.
    if (isAnyModalOpen.value) {
      closeModal()
    }
    const zIndex = options.aboveIntercom
      ? Math.max(nextModalZIndex.value, zIndexAboveIntercom)
      : nextModalZIndex.value
    modals.value[name] = {
      name,
      context,
      zIndex
    }
    if (options.track) {
      trackOverlayInteraction(name, 'modal', 'open', context)
    }
  }
  function closeModal (name = null) {
    if (name && typeof name === 'string') {
      delete modals.value[name]
    } else {
      if (latestModal.value) {
        delete modals.value[latestModal.value.name]
      }
    }
  }
  function openFlyout ({ name, context, reuse = false }) {
    // If the flyout is already open as the latest flyout, just update the context.
    if (reuse && flyoutName.value === name) {
      flyouts.value[name].context = context
      return
    }
    // Currently, we only track one open flyout at a time.
    if (isAnyFlyoutOpen.value) {
      closeFlyout()
    }
    flyouts.value[name] = {
      name,
      context,
      zIndex: nextFlyoutZIndex.value
    }
  }
  function closeFlyout (name = null) {
    if (name && typeof name === 'string') {
      delete flyouts.value[name]
    } else {
      if (latestFlyout.value) {
        delete flyouts.value[latestFlyout.value.name]
      }
    }
  }
  function openDrawer (name, options = {} ) {
    const { context, minimized = false } = options
    drawers.value[name] = {
      name,
      context: { ...context },
      minimized,
      zIndex: nextDrawerZIndex.value
    }
  }
  function closeDrawer (name = null) {
    if (name && typeof name === 'string') {
      delete drawers.value[name]
    } else if (latestDrawer.value) {
      delete drawers.value[latestDrawer.value.name]
    }
  }
  
  function minimizeDrawer (name) {
    if (name && typeof name === 'string') {
      drawers.value[name] = {
        name,
        context: { ...drawers.value[name].context },
        minimized: true,
        zIndex: nextDrawerZIndex.value
      }
    }
  }
  function closeLatest () {
    if (!latest.value) return false
    switch (latest.value.type) {
      case 'modal':
        closeModal(latest.value.name)
        break
      case 'flyout':
        closeFlyout(latest.value.name)
        break
      case 'drawer':
        closeDrawer(latest.value.name)
        break
      default:
        return false
    }
    return true
  }
  function closeAllModals () {
    modals.value = {}
  }
  function closeAllFlyouts () {
    flyouts.value = {}
  }
  function closeAllDrawers () {
    drawers.value = {}
  }
  function closeAll () {
    closeAllModals()
    closeAllFlyouts()
    closeAllDrawers()
  }

  return {
    modals,
    flyouts,
    drawers,
    drawerIsDragging,
    savedScrollPositions,
    modalName,
    modalContext,
    modalZIndex,
    flyoutName,
    flyoutContext,
    flyoutZIndex,
    isAnyModalOpen,
    isAnyDrawerOpen,
    isAnyDrawerPresent,
    isAnyFlyoutOpen,
    isAnyOverlayOpen,
    latestModal,
    latestFlyout,
    latest,
    baseModalZIndex,
    baseFlyoutZIndex,
    latestZIndex,
    nextModalZIndex,
    nextFlyoutZIndex,
    nextDrawerZIndex,
    drawerName,
    drawerContext,
    drawerZIndex,
    drawerMinimized,
    isModalOpen,
    isFlyoutOpen,
    openModal,
    closeModal,
    openFlyout,
    closeFlyout,
    closeLatest,
    closeAllModals,
    closeAllFlyouts,
    closeAll,
    openDrawer,
    closeDrawer,
    minimizeDrawer,
    closeAllDrawers
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useOverlaysStore, import.meta.hot))
